<template>
	<div>
		<!-- Displayed value -->
		<form-field-readonly
			@click="openModal"
			@remove:clicked="onUnselect"
			:hasRemove="computedValue ? true : false"
		>
			<div class="xs-12">
				<!-- If there is a relationship selected -->
				<template v-if="computedValue">
					<div class="fs3 c6 lh125">
						<span class="">{{ entityName }}</span> {{ $t("has_relationship") }}
						<span class=""
							>"{{ computedValue.type ? computedValue.type : "N/A" }}"</span
						>
						{{ $t("with") }}:
					</div>
					<div class="lh125">
						{{ computedValue.sourceName ? computedValue.sourceName : "N/A" }}
					</div>
				</template>
				<div v-else><slot name="empty">---</slot></div>
			</div>
		</form-field-readonly>

		<!-- Modal -->
		<core-modal :name="instanceKey" headline="Select Relationship">
			<div class="p4">
				<!-- Entity name-->
				<div class="mb4">
					<span class="fw7">{{ entityName }}</span>
					{{ $t("has_these_relationships") }}:
				</div>

				<!-- Selection -->
				<div
					v-for="(relationship, i) in computedRelationships"
					:key="i"
					class="r2 p2 mb2 cu2 bg5"
					:class="[
						{ 'bc2 b2': relationship.isSelected },
						{ 'bc7 b1': !relationship.isSelected }
					]"
					@click="onSelect(relationship)"
				>
					<div class="lh125 c6 fs3">
						<span class="fw7">{{ entityName }}</span> has relationship
						<span class="fw7"
							>"{{ relationship.type ? relationship.type : "N/A" }}"</span
						>
						with:
					</div>
					<div class="lh125 fw7 lh125">
						{{
							relationship.entity && relationship.entity.name
								? relationship.entity.name
								: "N/A"
						}}
					</div>
				</div>

				<!-- No data -->
				<div
					v-if="computedRelationships && computedRelationships.length === 0"
					class="xs-12 bg5 r2 b1 bc7 p4 ta2 fs3 c6 fw7 mb4"
				>
					{{ $t("errors.no_data") }}
				</div>

				<!-- Add new -->
				<div class="flex xs-12 xs-right pt4">
					<j-button type="tertiary" @click="isAdding = !isAdding">
						Add new relationship
					</j-button>
				</div>

				<!-- Add new box -->
				<div class="bg5 p4 b1 bc7 r2 mt2" v-if="isAdding">
					<div class="lh125 mb1">
						<span class="fw7">{{ entityName }}</span> will have a new
						relationship of type:
					</div>
					<j-base-input-text
						v-model="form.type"
						size="small"
						placeholder="e.g. employer, parent, spouse, owns"
					/>

					<!-- Entity -->
					<div class="mt6">with an entity:</div>
					<form-field-readonly @click="$modal.show(entityPickerKey)">
						<div v-if="form.targetName">{{ form.targetName }}</div>
						<div v-else class="c2 fw7">Select Entity</div>
					</form-field-readonly>

					<!-- Buttons -->
					<div
						class="flex mt2 xs-between pt4 bt1 bc7"
						v-if="form.targetId && form.type"
					>
						<j-button
							:isLoading="isAddingLoading"
							type="primary"
							size="xsmall"
							@click="createRelationship"
						>
							Add Relationship
						</j-button>
						<j-button
							:isDisabled="isAddingLoading"
							type="bordered"
							size="xsmall"
							@click="isAdding = false"
						>
							{{ $t("cancel") }}
						</j-button>
					</div>
				</div>
			</div>
		</core-modal>

		<!-- Entity picker -->
		<!-- Modal -->
		<core-modal
			:name="entityPickerKey"
			headline="Select Entity"
			:pivotX="0"
			:pivotY="0"
			:hasHeader="false"
		>
			<div style="height:100vh; overflow: scroll;">
				<entity-picker @entity:selected="onEntitySelect" />
			</div>
		</core-modal>
	</div>
</template>

<script>
import CoreModal from "@/modules/core/components/CoreModal"
import JButton from "@/components/JButton"
import JBaseInputText from "@/components/JBaseInputText"
import FormFieldReadonly from "../FormFieldReadonly.vue"
import EntityPicker from "../EntityPicker/EntityPicker"
import get from "lodash.get"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import { getFieldData } from "../utils/index.js"

export default {
	components: {
		CoreModal,
		JButton,
		JBaseInputText,
		FormFieldReadonly,
		EntityPicker
	},

	props: {
		/**
		 * Value represents `if` of relationship
		 */
		value: {
			type: String
		},

		/**
		 * Field from schema
		 */
		field: {
			type: Object,
			default: () => ({})
		}
	},

	data() {
		return {
			instanceKey: Math.random().toString(), // get unique key fro modal window
			entityPickerKey: Math.random().toString(),

			// For creating relationship
			isAdding: false,
			isAddingLoading: false,
			form: {
				targetName: "",
				targetId: "",
				type: ""
			}
		}
	},

	computed: {
		entity() {
			const entity = getFieldData("entity", this.field, this)
			if (!entity)
				console.error(
					`There is no field.entity in ${
						this.field.label
					}, it will cause errors for adding and updating entity.`,
					this.field
				)
			return entity
		},

		/**
		 * Based on this we do API calls
		 */
		entityId() {
			return this.entity?.id
		},

		/**
		 * When we need to display name of a source
		 */
		entityName() {
			return this.entity?.name
		},

		cache() {
			return getFieldData("relation", this.field, this)
		},

		computedRelationships() {
			return this.cache?.map(item => ({
				...item,
				isSelected: item?.id === this.value
			}))
		},

		computedValue() {
			// Find relationship in cache based on targetId and type
			const foundRelationship = this.cache?.find(item => item.id === this.value)

			// Return object
			if (!foundRelationship) return

			return {
				sourceId: foundRelationship?.entity?.id,
				sourceName: foundRelationship?.entity?.name,
				type: foundRelationship?.type
			}
		}
	},

	methods: {
		// When user wants to open a picker modal,
		// check if if we actually have `entity` to pick data for
		// If not, show message and don't show modal
		openModal() {
			if (!this.entity) {
				this.$store.dispatch("addMessage", {
					delay: 1500,
					message: {
						type: "neutral",
						text: "You have to select entity first"
					}
				})
				return
			}

			this.isAdding = false
			this.$modal.show(this.instanceKey)
		},

		// When user unselects
		onUnselect() {
			this.$emit("input", "")
			this.$modal.hide(this.instanceKey)
		},

		// When user selects relationship
		onSelect(relationship) {
			this.$emit("input", relationship.id)
			this.$modal.hide(this.instanceKey)
		},

		// When entity is selected in entity picker
		onEntitySelect({ id, name }) {
			this.form.targetId = id
			this.form.targetName = name
			this.$modal.hide(this.entityPickerKey)
		},

		async createRelationship() {
			const query = gql`
				mutation createRelationship($input: createLinkInput!) {
					addLink(linkInput: $input) {
						status
						errors
					}
				}
			`

			this.isAddingLoading = true
			const res = await api({
				query,
				variables: {
					input: {
						sourceId: this.entityId,
						link: this.form.type,
						targetId: this.form.targetId
					}
				}
			})

			// Errors
			if (res?.data?.addLink?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addLink?.errors?.[0] || this.$t("errors.general")
				)
				this.isAddingLoading = false
				return
			}

			// Success
			// Refetch case to update all possible entities in the graph with new data
			await this.$store.dispatch("caseManagement/fetchCase", {
				caseId: this.$store.state.caseManagement.caseId
			})

			// Stop loading
			this.isAddingLoading = false

			// Select relationship added
			const newRelationship = this.computedRelationships.find(
				rel =>
					rel.type === this.form.type && rel.entity?.id === this.form.targetId
			)
			if (newRelationship) this.onSelect(newRelationship)

			// Reset form
			this.isAdding = false
			this.form = { targetId: "", targetName: "", type: "" }
		}
	}
}
</script>
